import React, { useState, useRef } from "react";
import jsPDF from "jspdf";
import { QRCodeCanvas } from "qrcode.react";

// Modal for Adding a New Customer
function AddCustomerModal({ onClose, onAddCustomer }) {
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const handleAddCustomer = () => {
    if (newCustomer.name && newCustomer.email && newCustomer.phone) {
      onAddCustomer(newCustomer);
      onClose();
    } else {
      alert("Please fill all fields!");
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Add Customer</h2>
        <div className="space-y-4">
          <input
            type="text"
            placeholder="Name"
            value={newCustomer.name}
            onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
            className="w-full border p-2 rounded-md"
          />
          <input
            type="email"
            placeholder="Email"
            value={newCustomer.email}
            onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
            className="w-full border p-2 rounded-md"
          />
          <input
            type="tel"
            placeholder="Phone"
            value={newCustomer.phone}
            onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
            className="w-full border p-2 rounded-md"
          />
        </div>
        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={handleAddCustomer}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-500"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Sales Form Component
function SalesForm({ sale, onClose, onSave }) {
  const [formData, setFormData] = useState(
    sale || {
      customer_name: "",
      customer_details: null,
      products: [{ product_name: "", quantity: 1, unit_price: 0 }],
      amount_paid: 0,
      payment_status: "Pending",
      delivery_status: "Scheduled",
      notes: "",
    }
  );

  const [qrCodeData, setQrCodeData] = useState("");
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const scrollableContainerRef = useRef(null);
  const productRefs = useRef([]);

  const handleCustomerSearch = (value) => {
    const dummyCustomer = {
      name: "John Doe",
      email: "john@example.com",
      phone: "1234567890",
    };

    if (value === "John" || value === "1234567890") {
      setFormData({ ...formData, customer_name: value, customer_details: dummyCustomer });
    } else {
      setFormData({ ...formData, customer_name: value, customer_details: null });
    }
  };

  const handleAddCustomer = (newCustomer) => {
    setFormData({
      ...formData,
      customer_name: newCustomer.name,
      customer_details: newCustomer,
    });
  };

  const handleAddProduct = () => {
    setFormData((prevData) => ({
      ...prevData,
      products: [...prevData.products, { product_name: "", quantity: 1, unit_price: 0 }],
    }));

    setTimeout(() => {
      const lastProductIndex = productRefs.current.length - 1;
      if (productRefs.current[lastProductIndex]) {
        productRefs.current[lastProductIndex].scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 0);
  };

  const calculateTotalAmount = () =>
    formData.products.reduce((sum, product) => sum + product.quantity * product.unit_price, 0);

  const handlePaymentStatus = (updatedAmountPaid = formData.amount_paid) => {
    const totalAmount = calculateTotalAmount();
    let paymentStatus = "Pending";

    if (totalAmount === 0 && updatedAmountPaid === 0) {
      paymentStatus = "Not Paid";
    } else if (totalAmount !== 0 && updatedAmountPaid === totalAmount) {
      paymentStatus = "Paid";
    }

    setFormData({ ...formData, amount_paid: updatedAmountPaid, payment_status: paymentStatus });


    // Reset QR Code and share button if total amount changes
    if (qrCodeData && totalAmount !== calculateTotalAmount()) {
        setQrCodeData("");
      }
    };

    const handleAmountPaidChange = (value) => {
        const updatedAmountPaid = +value;
        handlePaymentStatus(updatedAmountPaid);
      };
  

  const handleGenerateQRCode = () => {
    const totalAmount = calculateTotalAmount();
    const upiLink = `upi://pay?pa=your-upi-id@upi&pn=${formData.customer_name || "Customer"}&mc=0000&tid=1234567890&tr=1234567890&tn=Payment for Order&am=${totalAmount}&cu=INR`;
    setQrCodeData(upiLink);
  };

  const handleShareQRCode = () => {
    const canvas = document.querySelector("canvas");
    if (!canvas) return;
    const qrCodeImage = canvas.toDataURL("image/png");
    const message = `Hi ${formData.customer_name || "Customer"},\n\nPlease pay the total amount of ₹${calculateTotalAmount()} using the QR code below:\n`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}&media=${qrCodeImage}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleShareReceipt = (type) => {
    const totalAmount = calculateTotalAmount();
    const productsText = formData.products
      .map(
        (product, index) =>
          `${index + 1}. ${product.product_name} - Quantity: ${product.quantity}, Unit Price: ₹${product.unit_price}, Total: ₹${product.quantity * product.unit_price}`
      )
      .join("\n");

    const message = encodeURIComponent(
      `Sales Receipt\n\nCustomer Name: ${formData.customer_name}\n\nProducts:\n${productsText}\n\nTotal Amount: ₹${totalAmount}\nAmount Paid: ₹${formData.amount_paid}\nPayment Status: ${formData.payment_status}\nDelivery Status: ${formData.delivery_status}`
    );

    if (type === "whatsapp") {
      const whatsappUrl = `https://wa.me/?text=${message}`;
      window.open(whatsappUrl, "_blank");
    } else if (type === "email") {
      const mailtoUrl = `mailto:?subject=Sales Receipt&body=${message}`;
      window.open(mailtoUrl, "_blank");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const totalAmount = calculateTotalAmount();
    onSave({ ...formData, total_amount: totalAmount });
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl flex h-[90vh]">
        {/* Left Section: Customer Information */}
        <div className="w-1/5 bg-gray-50 border-r p-4 flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Customer</h3>
            <label className="block text-sm font-medium text-gray-600">Search</label>
            <input
              type="text"
              placeholder="Name or Phone"
              value={formData.customer_name}
              onChange={(e) => handleCustomerSearch(e.target.value)}
              className="w-full border p-2 rounded-md mb-4"
            />
            {formData.customer_details && (
              <div className="bg-gray-100 border rounded-md p-4">
                <p className="text-sm font-medium text-gray-600">Name</p>
                <p className="text-sm text-gray-800 mb-2">{formData.customer_details.name}</p>
                <p className="text-sm font-medium text-gray-600">Email</p>
                <p className="text-sm text-gray-800 mb-2">{formData.customer_details.email}</p>
                <p className="text-sm font-medium text-gray-600">Phone</p>
                <p className="text-sm text-gray-800">{formData.customer_details.phone}</p>
              </div>
            )}
          </div>
          <button
            onClick={() => setShowAddCustomerModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-500 mt-4"
          >
            Add Customer
          </button>
        </div>

        {/* Right Section: Sales Form */}
        <div className="w-4/5 flex flex-col">
          {/* Header */}
          <div className="p-4 border-b bg-gray-100 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">
              {sale ? "Edit Sale" : "Add Sale"}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-gray-900 text-2xl"
            >
              &times;
            </button>
          </div>

          {/* Scrollable Sales Content */}
          <div ref={scrollableContainerRef} className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Products Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Products</h3>
              {formData.products.map((product, index) => (
                <div
                  key={index}
                  ref={(el) => (productRefs.current[index] = el)}
                  className="grid grid-cols-6 gap-4 items-center mb-4"
                >
                  <input
                    type="text"
                    placeholder="Product Name"
                    value={product.product_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        products: formData.products.map((p, i) =>
                          i === index ? { ...p, product_name: e.target.value } : p
                        ),
                      })
                    }
                    className="col-span-2 border p-2 rounded-md"
                    required
                  />
                  <input
                    type="number"
                    placeholder="Quantity"
                    value={product.quantity}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        products: formData.products.map((p, i) =>
                          i === index ? { ...p, quantity: +e.target.value } : p
                        ),
                      })
                    }
                    className="border p-2 rounded-md"
                    min="1"
                    required
                  />
                  <input
                    type="number"
                    placeholder="Unit Price"
                    value={product.unit_price}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        products: formData.products.map((p, i) =>
                          i === index ? { ...p, unit_price: +e.target.value } : p
                        ),
                      })
                    }
                    className="border p-2 rounded-md"
                    min="0"
                    required
                  />
                  <p className="text-gray-800 font-semibold">
                    ₹{product.quantity * product.unit_price || 0}
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        products: formData.products.filter((_, i) => i !== index),
                      })
                    }
                    className="text-red-600 hover:underline"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddProduct}
                className="text-blue-600 hover:underline"
              >
                Add Product
              </button>
            </div>

            {/* Notes Section */}
            <div>
              <label className="block text-sm font-medium text-gray-600">Notes</label>
              <textarea
                placeholder="Enter any additional notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                className="w-full border p-2 rounded-md"
                rows="3"
              ></textarea>
            </div>
          </div>

{/* Footer */}
<div className="p-4 border-t bg-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between lg:space-y-0 space-y-4 min-h-[120px] lg:gap-6">
  {/* Left Section (75%): Payment Details and Buttons */}
  <div className="w-full lg:w-3/4 flex flex-col space-y-4">
    {/* First Row: Payment Details */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 items-center text-sm">
      <div>
        <p className="font-medium text-gray-600">Total Amount</p>
        <p className="font-bold text-gray-800">₹{calculateTotalAmount()}</p>
      </div>
      <div>
        <label className="font-medium text-gray-600">Amount Paid</label>
        <input
  type="number"
  value={formData.amount_paid}
  onChange={(e) => handleAmountPaidChange(e.target.value)}
  className="w-full border p-1.5 rounded-md"
  min="0"
/>
      </div>
      <div>
        <p className="font-medium text-gray-600">Payment Status</p>
        <p className="font-bold text-gray-800">{formData.payment_status}</p>
      </div>
      <div>
        <label className="font-medium text-gray-600">Delivery Status</label>
        <select
          value={formData.delivery_status}
          onChange={(e) =>
            setFormData({ ...formData, delivery_status: e.target.value })
          }
          className="w-full border p-1.5 rounded-md"
        >
          <option value="Scheduled">Scheduled</option>
          <option value="Delivered">Delivered</option>
          <option value="Pending">Pending</option>
        </select>
      </div>
    </div>

    {/* Second Row: Action Buttons */}
    <div className="flex flex-col lg:flex-row items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-3">
      <button
        type="button"
        className="bg-gray-600 text-white px-3 py-1.5 text-sm rounded-md hover:bg-gray-500 w-full lg:w-auto flex-1"
        onClick={onClose}
      >
        Cancel
      </button>
      <button
        type="submit"
        className="bg-blue-600 text-white px-3 py-1.5 text-sm rounded-md hover:bg-blue-500 w-full lg:w-auto flex-1"
        onClick={handleSubmit}
      >
        Save
      </button>
      <div className="relative group w-full lg:w-auto flex-1">
        <button
          type="button"
          className="bg-yellow-600 text-white px-3 py-1.5 text-sm rounded-md hover:bg-yellow-500 w-full"
        >
          Share Receipt
        </button>
        <div className="absolute hidden group-hover:flex flex-col bg-white border rounded-md shadow-lg mt-2">
          <button
            className="text-gray-800 px-4 py-2 hover:bg-gray-100 text-left"
            onClick={() => handleShareReceipt("email")}
          >
            Share via Email
          </button>
          <button
            className="text-gray-800 px-4 py-2 hover:bg-gray-100 text-left"
            onClick={() => handleShareReceipt("whatsapp")}
          >
            Share via WhatsApp
          </button>
        </div>
      </div>
      <button
        onClick={handleGenerateQRCode}
        className="bg-green-600 text-white px-3 py-1.5 text-sm rounded-md hover:bg-green-500 w-full lg:w-auto flex-1"
      >
        Generate QR Code
      </button>
    </div>
  </div>

  {/* Right Section (25%): QR Code */}
  <div className="w-full lg:w-1/4 flex items-center justify-center lg:justify-start space-x-3">
    {qrCodeData ? (
      <>
        {/* QR Code */}
        <QRCodeCanvas value={qrCodeData} size={80} />
        {/* Share Icon */}
        <div
          onClick={handleShareQRCode}
          className="cursor-pointer flex items-center justify-center bg-yellow-600 rounded-full p-2 hover:bg-yellow-500"
          title="Share QR Code"
        >
          {/* Lucide React Share Icon */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 12v4c0 1.104.896 2 2 2h12c1.104 0 2-.896 2-2v-4m-9-7l4 4m0 0l-4 4m4-4H4"
            />
          </svg>
        </div>
      </>
    ) : (
      // Placeholder to maintain footer size consistency
      <div className="flex items-center space-x-3">
        <div className="h-[80px] w-[80px] bg-gray-200 rounded-md"></div>
      </div>
    )}
  </div>
</div> 
</div>
</div>
   {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <AddCustomerModal
          onClose={() => setShowAddCustomerModal(false)}
          onAddCustomer={handleAddCustomer}
        />
      )}
    </div>
  );
}

export default SalesForm;